import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError, API_BASE, authHeaders, withAuthParam } from "@/lib/api";
import { Search, Download, Eye, Trash2, X, Plus, Tag as TagIcon, LayoutGrid, List as ListIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = ["New", "Reviewed", "Contacted", "Closed"];

export default function AdminCandidates() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState("table"); // table | grouped
  const [groupedData, setGroupedData] = useState([]);
  const [meta, setMeta] = useState({ disciplines: [], availability_options: [], discipline_groups: {} });

  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [availability, setAvailability] = useState("");
  const [status, setStatus] = useState("");
  const [tag, setTag] = useState("");
  const [location, setLocation] = useState("");
  const [rateMin, setRateMin] = useState("");
  const [rateMax, setRateMax] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tags, setTags] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/candidates/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (discipline) params.discipline = discipline;
      if (availability) params.availability = availability;
      if (status) params.status = status;
      if (tag) params.tag = tag;
      if (location) params.location = location;
      if (rateMin) params.rate_min = rateMin;
      if (rateMax) params.rate_max = rateMax;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/candidates", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, discipline, availability, status, tag, location, rateMin, rateMax, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view === "grouped") {
      api.get("/candidates/grouped").then((r) => setGroupedData(r.data)).catch(() => {});
    }
  }, [view, items]);

  useEffect(() => {
    api.get("/candidates/tags").then((r) => setTags(r.data)).catch(() => {});
  }, [items]);

  const downloadCv = (c) => {
    const url = withAuthParam(`${API_BASE}/candidates/${c.id}/cv`);
    window.open(url, "_blank");
  };

  const exportCsv = () => {
    fetch(`${API_BASE}/candidates/export`, { headers: authHeaders() })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "nexra_candidates.csv";
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const deleteCandidate = async (id) => {
    if (!window.confirm("Remove this professional from the Nexra Network?")) return;
    try {
      await api.delete(`/candidates/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <AdminLayout
      title="Nexra Delivery Network"
      subtitle={`${total} professional${total === 1 ? "" : "s"} in the curated network`}
      actions={
        <>
          <div className="inline-flex border border-border bg-white">
            <button
              onClick={() => setView("table")}
              className={`px-3 py-2.5 text-xs font-mono uppercase tracking-wider flex items-center gap-2 ${view === "table" ? "bg-obsidian text-white" : "text-muted-foreground hover:bg-muted"}`}
              data-testid="view-table"
            >
              <ListIcon className="h-3.5 w-3.5" /> Table
            </button>
            <button
              onClick={() => setView("grouped")}
              className={`px-3 py-2.5 text-xs font-mono uppercase tracking-wider flex items-center gap-2 border-l border-border ${view === "grouped" ? "bg-obsidian text-white" : "text-muted-foreground hover:bg-muted"}`}
              data-testid="view-grouped"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> By Discipline
            </button>
          </div>
          <button onClick={exportCsv} className="btn-secondary" data-testid="candidates-export-csv">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </>
      }
    >
      <div className="border border-border bg-white p-4 mb-4" data-testid="candidates-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input-swiss pl-10"
              placeholder="Search name, email, skills, discipline..."
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="candidates-search"
            />
          </div>
          <select className="input-swiss md:col-span-2" value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="filter-discipline">
            <option value="">All disciplines</option>
            {(meta.all_disciplines || meta.disciplines || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="input-swiss md:col-span-2" value={availability} onChange={(e) => setAvailability(e.target.value)} data-testid="filter-availability">
            <option value="">All availability</option>
            {(meta.availability_options || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="input-swiss md:col-span-2" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="input-swiss md:col-span-2" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="filter-tag">
            <option value="">All tags</option>
            {tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="date" className="input-swiss md:col-span-2" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <input type="date" className="input-swiss md:col-span-2" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          <input className="input-swiss md:col-span-4" placeholder="Location (e.g., London)" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="filter-location" />
          <input type="number" className="input-swiss md:col-span-2" placeholder="Rate min" value={rateMin} onChange={(e) => setRateMin(e.target.value)} data-testid="filter-rate-min" />
          <input type="number" className="input-swiss md:col-span-2" placeholder="Rate max" value={rateMax} onChange={(e) => setRateMax(e.target.value)} data-testid="filter-rate-max" />
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

      {view === "table" ? (
        <CandidateTable
          loading={loading}
          items={items}
          user={user}
          onView={setSelected}
          onDownload={downloadCv}
          onDelete={deleteCandidate}
        />
      ) : (
        <GroupedView
          groups={groupedData}
          onView={setSelected}
          onDownload={downloadCv}
        />
      )}

      {selected && (
        <CandidateDrawer
          candidate={selected}
          meta={meta}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => {
            setSelected(updated);
            setItems((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
          }}
          onDownload={() => downloadCv(selected)}
        />
      )}
    </AdminLayout>
  );
}

function CandidateTable({ loading, items, user, onView, onDownload, onDelete }) {
  return (
    <div className="border border-border bg-white overflow-x-auto" data-testid="candidates-table">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-obsidian text-white text-left">
          <tr>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Name</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Discipline</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Rate</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Availability</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Location</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Status</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium">Submitted</th>
            <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">Loading…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={8} className="py-16 text-center text-muted-foreground" data-testid="candidates-empty">No professionals match your filters.</td></tr>
          ) : items.map((c) => (
            <tr key={c.id} className="hover:bg-muted" data-testid={`candidate-row-${c.id}`}>
              <td className="px-4 py-3">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.email}</div>
              </td>
              <td className="px-4 py-3">
                <div className="font-medium text-sm">{c.discipline}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{c.discipline_group}</div>
              </td>
              <td className="px-4 py-3 font-mono text-sm">
                {c.rate} <span className="text-[10px] text-muted-foreground">/{c.rate_type === "hourly" ? "hr" : "day"}</span>
              </td>
              <td className="px-4 py-3 text-xs">{c.availability}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{c.location || "—"}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${
                  c.status === "New" ? "bg-execution text-white" :
                  c.status === "Reviewed" ? "bg-[#00C779] text-white" :
                  c.status === "Contacted" ? "bg-obsidian text-white" :
                  "bg-muted text-muted-foreground border border-border"
                }`}>
                  {c.status || "New"}
                </span>
              </td>
              <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{new Date(c.submitted_at).toLocaleDateString()}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <IconBtn onClick={() => onView(c)} testid={`candidate-view-${c.id}`} title="View"><Eye className="h-4 w-4" /></IconBtn>
                  <IconBtn onClick={() => onDownload(c)} testid={`candidate-download-${c.id}`} title="Download CV"><Download className="h-4 w-4" /></IconBtn>
                  {user?.role === "admin" && (
                    <IconBtn onClick={() => onDelete(c.id)} testid={`candidate-delete-${c.id}`} title="Delete" danger><Trash2 className="h-4 w-4" /></IconBtn>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupedView({ groups, onView, onDownload }) {
  return (
    <div className="space-y-6" data-testid="candidates-grouped">
      {groups.map((g) => (
        <div key={g.group} className="border border-border bg-white">
          <div className="px-5 py-4 bg-obsidian text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-heading text-lg font-semibold">{g.group}</span>
              <span className="chip-dark"><span className="h-1.5 w-1.5 bg-execution rounded-full" />{g.count}</span>
            </div>
          </div>
          {g.candidates.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No professionals in this group yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {g.candidates.map((c) => (
                <li key={c.id} className="px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center hover:bg-muted">
                  <div className="md:col-span-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </div>
                  <div className="md:col-span-2 text-sm">{c.discipline}</div>
                  <div className="md:col-span-2 font-mono text-sm">{c.rate}/{c.rate_type === "hourly" ? "hr" : "day"}</div>
                  <div className="md:col-span-3 text-xs">{c.availability}</div>
                  <div className="md:col-span-2 flex md:justify-end gap-1">
                    <IconBtn onClick={() => onView(c)} title="View"><Eye className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => onDownload(c)} title="Download CV"><Download className="h-4 w-4" /></IconBtn>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function IconBtn({ children, danger, testid, ...props }) {
  return (
    <button
      {...props}
      data-testid={testid}
      className={`h-8 w-8 flex items-center justify-center border border-border hover:border-obsidian transition-colors ${
        danger ? "hover:bg-destructive hover:border-destructive hover:text-white" : "hover:bg-obsidian hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function CandidateDrawer({ candidate, meta, onClose, onUpdated, onDownload }) {
  const [tagInput, setTagInput] = useState("");
  const [data, setData] = useState({
    tags: candidate.tags || [],
    notes: candidate.notes || "",
    discipline: candidate.discipline || "",
    rate: candidate.rate || "",
    rate_type: candidate.rate_type || "daily",
    availability: candidate.availability || "",
    location: candidate.location || "",
    status: candidate.status || "New",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/candidates/${candidate.id}`, data);
      onUpdated(updated);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || data.tags.includes(t)) return;
    setData((d) => ({ ...d, tags: [...d.tags, t] }));
    setTagInput("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm flex justify-end" data-testid="candidate-drawer">
      <aside className="w-full max-w-2xl bg-white h-full overflow-y-auto border-l border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div className="overline">Network professional</div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">{candidate.name}</h2>
            <div className="mt-1 text-sm text-muted-foreground">{candidate.email} · {candidate.phone}</div>
            <div className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
              Submitted {new Date(candidate.submitted_at).toLocaleString()}
            </div>
          </div>

          <div className="border border-border p-4 grid grid-cols-2 gap-4 bg-muted/50">
            <Stat label="Discipline" value={`${candidate.discipline} · ${candidate.discipline_group}`} />
            <Stat label="Rate" value={`${candidate.rate} / ${candidate.rate_type}`} />
            <Stat label="Availability" value={candidate.availability} />
            <Stat label="Status" value={data.status} />
          </div>

          <div>
            <div className="overline mb-2">CV</div>
            <button onClick={onDownload} className="btn-secondary" data-testid="drawer-download-cv">
              <Download className="h-4 w-4" />
              {candidate.cv_original_filename || "Download CV"}
            </button>
          </div>

          <div>
            <div className="overline mb-2">Skills / Experience</div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed border border-border p-4 bg-muted">{candidate.skills}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Discipline" value={data.discipline} onChange={(v) => setData((d) => ({ ...d, discipline: v }))}>
              {[...new Set([...(meta.all_disciplines || meta.disciplines || []), candidate.discipline].filter(Boolean))].map((d) => <option key={d} value={d}>{d}</option>)}
            </SelectField>
            <SelectField label="Availability" value={data.availability} onChange={(v) => setData((d) => ({ ...d, availability: v }))}>
              {(meta.availability_options || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </SelectField>
            <div className="flex gap-2">
              <FieldText label="Rate" value={data.rate} onChange={(v) => setData((d) => ({ ...d, rate: v }))} />
              <SelectField label="Type" value={data.rate_type} onChange={(v) => setData((d) => ({ ...d, rate_type: v }))}>
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
              </SelectField>
            </div>
            <SelectField label="Status" value={data.status} onChange={(v) => setData((d) => ({ ...d, status: v }))}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </SelectField>
            <FieldText label="Location" value={data.location} onChange={(v) => setData((d) => ({ ...d, location: v }))} />
          </div>

          <div>
            <div className="overline mb-2">Tags</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {data.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-3 py-1 bg-obsidian text-white">
                  <TagIcon className="h-3 w-3" /> {t}
                  <button onClick={() => setData((d) => ({ ...d, tags: d.tags.filter((x) => x !== t) }))} className="hover:text-execution">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="Add tag (e.g., senior, banking)"
                className="input-swiss" data-testid="drawer-tag-input"
              />
              <button onClick={addTag} className="btn-secondary" data-testid="drawer-tag-add"><Plus className="h-4 w-4" /></button>
            </div>
          </div>

          <div>
            <div className="overline mb-2">Private notes</div>
            <textarea
              value={data.notes} onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
              className="input-swiss min-h-[120px]"
              placeholder="Internal notes, interview feedback, project fit..."
              data-testid="drawer-notes"
            />
          </div>

          <div className="pt-4 border-t border-border flex justify-end">
            <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="drawer-save">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function FieldText({ label, value, onChange }) {
  return (
    <label className="block flex-1">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</span>
      <input className="input-swiss" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</span>
      <select className="input-swiss" value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </label>
  );
}
